import moment from 'moment';
import {
	failureActionHandler,
	requestActionHandler,
	successActionHandler,
} from 'store';
import injectReducer from 'store/injectReducer';
import { RoleTypes, IsoString } from 'models';
import { ISurveyCardList } from 'modules/admin-panel/models';
import {
	ISettingsReducer,
	ISettingsPayload,
	SettingsResultTypes,
	SurveyTrackingResultTypes,
	ISurveyRoundResponse,
	ISurveyTrackingList,
	IEmailTemplate,
	IClientData,
	IEmailTemplateParams,
} from 'modules/admin-panel/pages/settings/models';
import { mapSurveyDates } from 'modules/admin-panel/pages/settings/helpers';
import * as types from 'modules/admin-panel/pages/settings/ActionTypes';
import { IClientTemplateParams } from './models/email-templates';

export const InitialSettingsReducer: ISettingsReducer = {
	profilePictureUrl: '',
	profilePictureKey: '',
	firstName: '',
	lastName: '',
	role: RoleTypes.NONE,
	phoneNumber: '',
	email: '',
	partner: {
		name: '',
		shortName: '',
		logoUrl: '',
	},
	client: {
		shortName: '',
		logoUrl: '',
	},
	occupation: '',
	surveyData: {
		graphicData: {},
		lastCreated: [],
		lastSent: [],
	},
	isLoading: false,
};

export default injectReducer(InitialSettingsReducer, {
	[types.GET_USER_DETAILS.REQUEST]: requestActionHandler,
	[types.UPDATE_USER_DETAILS.REQUEST]: requestActionHandler,
	[types.DELETE_USER_BY_THEMSELVES.REQUEST]: requestActionHandler,
	[types.RESET_PASSWORD_BY_ADMIN.REQUEST]: requestActionHandler,
	[types.DELETE_USER_BY_ADMIN.REQUEST]: requestActionHandler,
	[types.GET_TRACKING_SURVEY_LIST.REQUEST]: requestActionHandler,
	[types.GET_ALL_TRACKING_SURVEY_LIST.REQUEST]: requestActionHandler,
	[types.POST_USER_PHOTO.REQUEST]: requestActionHandler,
	[types.GET_STOP_SURVEY_ROUND.REQUEST]: requestActionHandler,
	[types.GET_STOP_SURVEY.REQUEST]: requestActionHandler,
	[types.DELETE_SURVEY.REQUEST]: requestActionHandler,
	[types.GET_SURVEY_SENT.REQUEST]: requestActionHandler,
	[types.GET_SEND_NOTIFICATIONS.REQUEST]: requestActionHandler,
	[types.GET_SURVEY_ROUND.REQUEST]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...state,
		isRoundLoading: true,
	}),

	[types.GET_EMAIL_TEMPLATES_DATA.REQUEST]: requestActionHandler,
	[types.GET_CLIENT_INVITATION_TEMPLATE.REQUEST]: requestActionHandler,
	[types.GET_CLIENT_REMINDER_TEMPLATE.REQUEST]: requestActionHandler,
	[types.POST_EMAIL_TEMPLATE.REQUEST]: requestActionHandler,
	[types.REPLACE_EMAIL_TEMPLATE.REQUEST]: requestActionHandler,

	[types.GET_USER_DETAILS.FAILURE]: failureActionHandler,
	[types.UPDATE_USER_DETAILS.FAILURE]: failureActionHandler,
	[types.DELETE_USER_BY_THEMSELVES.FAILURE]: failureActionHandler,
	[types.RESET_PASSWORD_BY_ADMIN.FAILURE]: failureActionHandler,
	[types.DELETE_USER_BY_ADMIN.FAILURE]: failureActionHandler,
	[types.GET_TRACKING_SURVEY_LIST.FAILURE]: failureActionHandler,
	[types.GET_ALL_TRACKING_SURVEY_LIST.FAILURE]: failureActionHandler,
	[types.POST_USER_PHOTO.FAILURE]: failureActionHandler,
	[types.GET_STOP_SURVEY_ROUND.FAILURE]: failureActionHandler,
	[types.GET_STOP_SURVEY.FAILURE]: failureActionHandler,
	[types.DELETE_SURVEY.FAILURE]: failureActionHandler,
	[types.GET_SURVEY_SENT.FAILURE]: failureActionHandler,

	[types.GET_EMAIL_TEMPLATES_DATA.FAILURE]: failureActionHandler,
	[types.GET_CLIENT_INVITATION_TEMPLATE.FAILURE]: failureActionHandler,
	[types.GET_CLIENT_REMINDER_TEMPLATE.FAILURE]: failureActionHandler,
	[types.POST_EMAIL_TEMPLATE.FAILURE]: failureActionHandler,
	[types.REPLACE_EMAIL_TEMPLATE.FAILURE]: failureActionHandler,

	[types.GET_SURVEY_ROUND.FAILURE]: (
		state: ISettingsReducer,
		{ payload }: { payload: Error },
	): ISettingsReducer => ({
		...state,
		error: payload,
		isRoundLoading: false,
	}),

	[types.EDIT_SURVEY_ROUND.FAILURE]: (
		state: ISettingsReducer,
		{ payload }: { payload: Error },
	): ISettingsReducer => ({
		...state,
		roundEditingError: payload,
		isLoading: false,
	}),

	[types.GET_SEND_NOTIFICATIONS.FAILURE]: (
		state: ISettingsReducer,
		{ payload }: { payload: Error },
	): ISettingsReducer => ({
		...state,
		notificationError: payload,
		isLoading: false,
	}),

	[types.RESET_NOTIFICATION_ERROR]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...state,
		notificationError: undefined,
	}),

	[types.CREATE_USER_PASSWORD.SUCCESS]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...successActionHandler(state),
	}),

	[types.GET_STOP_SURVEY_ROUND.SUCCESS]: (state: ISettingsReducer) => ({
		...successActionHandler(state),
		result: SurveyTrackingResultTypes.surveyRoundStopped,
	}),

	[types.GET_STOP_SURVEY.SUCCESS]: (state: ISettingsReducer) => ({
		...successActionHandler(state),
		result: SurveyTrackingResultTypes.surveyStopped,
	}),

	[types.DELETE_SURVEY.SUCCESS]: (state: ISettingsReducer) => ({
		...successActionHandler(state),
		result: SurveyTrackingResultTypes.surveyDeleted,
	}),

	[types.GET_SEND_NOTIFICATIONS.SUCCESS]: (state: ISettingsReducer) => ({
		...successActionHandler(state),
	}),

	[types.RESET_RESULT]: (state: ISettingsReducer): ISettingsReducer => ({
		...successActionHandler(state),
		result: undefined,
	}),

	[types.RESET_ERROR]: (state: ISettingsReducer): ISettingsReducer => ({
		...state,
		error: undefined,
		isLoading: false,
	}),

	[types.RESET_PASSWORD_BY_ADMIN.SUCCESS]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...successActionHandler(state),
		result: SettingsResultTypes.passwordChangedByAdmin,
	}),

	[types.DELETE_USER_BY_ADMIN.SUCCESS]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...successActionHandler(state),
		result: SettingsResultTypes.userDeletedByAdmin,
	}),

	[types.POST_USER_PHOTO.SUCCESS]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...successActionHandler(state),
		result: SettingsResultTypes.photoChanged,
	}),

	[types.GET_USER_DETAILS.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: ISettingsPayload },
	): ISettingsReducer => ({
		...successActionHandler(state),
		...payload,
	}),

	[types.UPDATE_USER_DETAILS.SUCCESS]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...successActionHandler(state),
		result: SettingsResultTypes.userDetailsUpdated,
	}),

	[types.DELETE_USER_BY_THEMSELVES.SUCCESS]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...successActionHandler(state),
		result: SettingsResultTypes.userDeletedByThemselves,
	}),

	[types.GET_LAST_CREATED_SURVEY_LIST.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: ISurveyCardList },
	): ISettingsReducer => ({
		...state,
		surveyData: {
			...state.surveyData,
			lastCreated: payload,
			lastSent: [...payload].sort((a, b) =>
				moment(a.startDate).unix() < moment(b.startDate).unix() ? 1 : -1,
			),
		},
	}),

	[types.GET_SURVEY_SENT.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: Record<IsoString, number> },
	): ISettingsReducer => ({
		...state,
		surveyData: {
			...state.surveyData,
			graphicData: payload,
		},
	}),

	[types.GET_TRACKING_SURVEY_LIST.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: ISurveyTrackingList },
	): ISettingsReducer => ({
		...successActionHandler(state),
		surveyList: payload,
	}),

	[types.GET_ALL_TRACKING_SURVEY_LIST.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: ISurveyTrackingList },
	): ISettingsReducer => ({
		...successActionHandler(state),
		surveyList: payload,
		allSurveys: payload,
	}),

	[types.EDIT_SURVEY_ROUND.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: ISurveyRoundResponse },
	): ISettingsReducer => {
		const { surveyList, allSurveys } = state;
		const newSurveyList = surveyList
			? {
					...surveyList,
					content: mapSurveyDates(surveyList.content, payload),
			  }
			: undefined;
		const newAllSurveys = allSurveys
			? {
					...allSurveys,
					content: mapSurveyDates(allSurveys.content, payload),
			  }
			: undefined;

		return {
			...successActionHandler(state),
			allSurveys: newAllSurveys,
			surveyList: newSurveyList,
			isRoundEdited: true,
		};
	},

	[types.GET_SURVEY_ROUND.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: ISurveyRoundResponse },
	): ISettingsReducer => ({
		...successActionHandler(state),
		editableRound: payload,
		isRoundLoading: false,
	}),

	[types.RESET_EDITABLE_ROUND]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...state,
		editableRound: undefined,
	}),

	[types.RESET_IS_ROUND_EDITED]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...state,
		isRoundEdited: false,
	}),

	[types.RESET_ROUND_EDITING_ERROR]: (
		state: ISettingsReducer,
	): ISettingsReducer => ({
		...state,
		roundEditingError: undefined,
	}),

	// EMAIL TEMPLATES EDITING
	[types.GET_EMAIL_TEMPLATES_DATA.SUCCESS]: (
		state: ISettingsReducer,
		{
			payload: { templateData, clientData },
		}: {
			payload: {
				templateData: IEmailTemplate[];
				clientData: IClientData;
			};
		},
	): ISettingsReducer => ({
		...state,
		isLoading: false,
		defaultEmailTemplates: templateData,
		clientData,
	}),
	[types.GET_CLIENT_INVITATION_TEMPLATE.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: IClientTemplateParams },
	): ISettingsReducer => ({
		...successActionHandler(state),
		...payload,
	}),
	[types.GET_CLIENT_REMINDER_TEMPLATE.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: IClientTemplateParams },
	): ISettingsReducer => ({
		...successActionHandler(state),
		...payload,
	}),
	[types.POST_EMAIL_TEMPLATE.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: IEmailTemplateParams },
	): ISettingsReducer => ({
		...successActionHandler(state),
		...payload,
		isEmailTemplateUpdated: true,
	}),
	[types.REPLACE_EMAIL_TEMPLATE.SUCCESS]: (
		state: ISettingsReducer,
		{ payload }: { payload: IEmailTemplateParams },
	): ISettingsReducer => ({
		...successActionHandler(state),
		...payload,
		isEmailTemplateCreated: true,
	}),
	[types.RESET_EMAIL_EDITING_STATUSES]: (state: ISettingsReducer) => ({
		...successActionHandler(state),
		isEmailTemplateCreated: false,
		isEmailTemplateUpdated: false,
	}),
});
